import React, { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetClose, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { getPatientSchema } from '@/validators/patient.schema';
import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { State } from '@/models/state.model';
import { stateService } from '@/services/stateService';
import { patientService } from '@/services/patientService';
import { useToast } from '@/hooks/use-toast';
import { practitionerService } from '@/services/practitionerService';
import { format } from 'date-fns';
import { Patient } from '@/lib/types';
import { Form } from '../ui/form';
import { AddressData } from '../ui/AddressInput';
import { smartService } from '@/services/smart.service';
import { getSession } from '@/lib/smartSession';
import { PatientFormData } from './patientForm.types';
import BasicPatientInfoSection from './PatientFormBasicInfoSection';
import ContactInformationSection from './PatientFormContactInformationSection';
import AddressSection from './PatientFormAddressSection';
import ClinicalInformationSection from './PatientFormClinicalInformationSection';
import MedicalHistorySection from './PatientFormMedicalHistorySection';
import AssignmentSection from './PatientFormAssignmentSection';
import InsuranceSection from './PatientFormInsuranceSection';
import { useClinic } from '@/contexts/ClinicContext';

interface PatientFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patient?: Patient | null;
  assignedPractitioner?: string;
  assignedClinic?: string;
  onSubmit: (data: unknown) => void;
}

const FIELD_LABELS: Record<string, string> = {
  first_name: 'First Name',
  middle_name: 'Middle Name',
  last_name: 'Last Name',
  gender: 'Gender',
  dob: 'Date of Birth',
  species: 'Species',
  animal_owner_name: 'Animal Owner Name',
  weight: 'Weight',
  height: 'Height',
  blood_pressure_systolic: 'Blood Pressure (Systolic)',
  blood_pressure_diastolic: 'Blood Pressure (Diastolic)',
  note: 'Patient Note',
  medication_allergies: 'Medication Allergies',
  no_known_allergies: 'No Known Allergies',
  medical_history: 'Medical History',
  status: 'Status',
  practitioner_id: 'Practitioner',
  assignedClinic: 'Clinic',
  contacts: 'Contact Information',
  addresses: 'Address',
  insurance_carrier: 'Insurance Carrier',
  insurance_member_id: 'Insurance Member ID',
  insurance_group_id: 'Insurance Group ID',
};

const PatientFormDrawer = ({
  open,
  onOpenChange,
  patient = null,
  onSubmit,
  assignedPractitioner = undefined,
  assignedClinic = undefined,
}: PatientFormDrawerProps) => {
  const { clinic } = useClinic();
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingPatient, setIsFetchingPatient] = useState(false);
  const [notesRows, setNotesRows] = useState(8);

  const [states, setStates] = useState<State[]>([]);
  const [practitioners, setPractitioners] = useState([]);
  const { toast } = useToast();
  const isGuided = clinic?.clinic_archetype === 'guided';

  // Collapsible open state: clinical optional (collapsed by default); medication history required and open by default
  const [isClinicalOpen, setIsClinicalOpen] = useState(false);
  const [isMedicalOpen, setIsMedicalOpen] = useState(true);

  const patientSchema = getPatientSchema(isGuided);

  const createContactId = () => {
    if (typeof globalThis.crypto !== 'undefined' && 'randomUUID' in globalThis.crypto) {
      return globalThis.crypto.randomUUID();
    }
    return `contact_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  };

  const createContact = (key: string) => ({
    id: createContactId(),
    key,
    value: '',
    is_default: false,
  });

  const normalizeContacts = (contacts?: PatientFormData['contacts']) => {
    const baseContacts = contacts && contacts.length > 0 ? contacts : [createContact('email'), createContact('phone')];
    return baseContacts.map(contact => ({
      ...contact,
      id: contact.id ?? createContactId(),
    }));
  };

  const getDefaultValues = (): PatientFormData => ({
    first_name: '',
    middle_name: '',
    last_name: '',
    gender: '',
    dob: '',
    species: 'human',
    animal_owner_name: '',
    weight: '',
    weight_recorded_date: '',
    height: undefined,
    blood_pressure_systolic: '',
    blood_pressure_diastolic: '',
    blood_pressure_recorded_at: '',
    medication_allergies: [],
    no_known_allergies: false,
    medical_history: '',
    practitioner_id: assignedPractitioner || '',
    assignedClinic: assignedClinic,
    status: 'active',
    note: '',
    preferred_contact_methods: {
      email: true,
      sms: false,
      phone: false,
    },
    emergency_contact_phone: '',
    insurance_carrier: '',
    insurance_member_id: '',
    insurance_group_id: '',
    contacts: normalizeContacts(),
    addresses: [
      {
        addressLine1: '',
        addressLine2: '',
        state: '',
        city: '',
        postalCode: '',
        address_type: 'billing' as const,
      },
    ],
    shippingAddressEnabled: false,
    shippingSameAsBilling: true,
  });

  const form = useForm<PatientFormData>({
    resolver: zodResolver(patientSchema),
    defaultValues: getDefaultValues(),
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'contacts',
    keyName: 'fieldId',
  });

  const {
    fields: addressFields,
    append: appendAddress,
    remove: removeAddress,
  } = useFieldArray({
    control: form.control,
    name: 'addresses',
  });

  // Handle address change from AddressInput component
  const handleAddressChange = (index: number) => (addressData: AddressData) => {
    form.setValue(`addresses.${index}.addressLine1`, addressData.addressLine1, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
    form.setValue(`addresses.${index}.addressLine2`, addressData.addressLine2 || '', {
      shouldValidate: false,
      shouldDirty: true,
    });
    form.setValue(`addresses.${index}.city`, addressData.city, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
    form.setValue(`addresses.${index}.state`, addressData.state, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });

    form.setValue(`addresses.${index}.postalCode`, addressData.postalCode || '', {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const handleSavePatient = async (data: PatientFormData) => {
    try {
      const result = await patientService.savePatient(data);
      if (result?.success) {
        // Check for SMART session and sync patient to FHIR server if available
        const smartSession = getSession();
        if (smartSession?.fhirServer && smartSession?.accessToken) {
          try {
            const patientId =
              typeof result?.data === 'object' && result?.data !== null && 'id' in result.data
                ? (result.data as { id: string }).id
                : '';
            await smartService.savePatient(
              patientId,
              smartSession.fhirServer,
              smartSession.accessToken
            );
          } catch (fhirError) {
            console.error('Failed to sync patient to FHIR server:', fhirError);
            // Don't fail the entire operation if FHIR sync fails
            toast({
              title: 'Warning',
              description: 'Patient saved locally but failed to sync with FHIR server.',
              variant: 'default',
            });
          }
        }

        toast({
          title: 'Success',
          description: 'Patient was created successfully.',
          variant: 'success',
        });
        onSubmit(result);
        form.reset();
        onOpenChange(false);
      } else {
        toast({
          title: 'Error',
          description: result?.message ?? 'Patient creation failed. Please refresh and try again.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error?.message ?? 'Patient creation failed. Please refresh and try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdatePatient = async (id: string, data: PatientFormData) => {
    try {
      const result = await patientService.updatePatient(id, data);
      if (result?.success) {
        toast({
          title: 'Success',
          description: 'Patient was updated successfully.',
          variant: 'success',
        });
        onSubmit(result);
        form.reset();
        onOpenChange(false);
      } else {
        toast({
          title: 'Error',
          description: result?.message ?? 'Patient update failed. Please refresh and try again.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error?.message ?? 'Patient update failed. Please refresh and try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (data: PatientFormData) => {
    setIsLoading(true);

    // Sanitize height - ensure it's a valid number or undefined
    if (data.height !== undefined && data.height !== null) {
      const heightValue = parseFloat(String(data.height));
      data.height = isNaN(heightValue) ? undefined : heightValue;
    }

    // Sanitize weight - ensure it's a valid string or empty
    if (data.weight !== undefined && data.weight !== null) {
      const weightStr = String(data.weight).trim();
      data.weight = weightStr === '' || weightStr === 'NaN' ? '' : weightStr;
    }

    if (patient) {
      handleUpdatePatient(patient.id, data);
    } else {
      handleSavePatient(data);
    }
  };

  const handleValidationError = (errors: Record<string, unknown>) => {
    const errorKeys = Object.keys(errors);

    // Auto-expand collapsed sections that contain errors
    const clinicalFields = ['note'];
    const medicalFields = ['medication_allergies', 'no_known_allergies', 'medical_history'];

    if (errorKeys.some(k => clinicalFields.includes(k))) {
      setIsClinicalOpen(true);
    }
    if (errorKeys.some(k => medicalFields.includes(k))) {
      setIsMedicalOpen(true);
    }

    // Show toast with human-readable field names
    const fieldNames = errorKeys
      .map(k => FIELD_LABELS[k] || k)
      .join(', ');
    toast({
      title: 'Missing required fields',
      description: `Please complete: ${fieldNames}`,
      variant: 'destructive',
    });

    // Scroll to first error field
    setTimeout(() => {
      const firstError = document.querySelector('.text-red-500');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 150);
  };

  const handleResetForm = () => {
    form.reset(getDefaultValues());
    setNotesRows(8);
    setMedicalHistoryRows(8);
    setIsClinicalOpen(false);
    setIsMedicalOpen(true);
  };

  useEffect(() => {
    if (states.length === 0) {
      const states = stateService.getAllStates().then(result => {
        setStates(result.data?.states);
      });
    }
  }, [states]);

  useEffect(() => {
    if (assignedClinic && practitioners.length === 0) {
      practitionerService.getPractitionerDropdownByClinic(assignedClinic, {}).then(result => {
        const fetchedPractitioners = result?.data?.practitioners || [];
        setPractitioners(fetchedPractitioners);
        if (isGuided && fetchedPractitioners.length > 0) {
          form.setValue('practitioner_id', fetchedPractitioners[0]?.id);
        }
      });
    }
  }, [practitioners?.length, assignedClinic, isGuided]);

  // Watch for changes in medication_allergies and uncheck "no_known_allergies" if allergies are selected
  const medicationAllergies = form.watch('medication_allergies');
  const noKnownAllergies = form.watch('no_known_allergies');

  useEffect(() => {
    const allergies = medicationAllergies || [];
    if (allergies.length > 0 && noKnownAllergies) {
      form.setValue('no_known_allergies', false, { shouldValidate: true });
    }
  }, [medicationAllergies, noKnownAllergies, form]);

  // Fetch full patient details when editing
  useEffect(() => {
    const fetchPatientDetails = async () => {
      if (open && patient?.id) {
        setIsFetchingPatient(true);
        try {
          const result = await patientService.getPatientById(patient.id);
          if (result?.success && result?.data) {
            const patientData = result.data;
            form.reset({
              first_name: patientData.first_name || '',
              middle_name: patientData.middle_name || '',
              last_name: patientData.last_name || '',
              gender: patientData.gender || '',
              dob: patientData.dob || '',
              species: patientData.species || 'human',
              practitioner_id: patientData.practitioner_id,
              assignedClinic: patientData.clinic_id,
              status: patientData.status,
              note: patientData.note || '',
              contacts: normalizeContacts(patientData.contacts),
              addresses: patientData?.addresses?.length > 0 ? patientData?.addresses?.map((address: any) => ({
                addressLine1: address.line_1 || '',
                addressLine2: address.line_2 || '',
                addressLine3: address.line_3 || '',
                state: address.stateInfo?.code || address.state || '',
                city: address.city || '',
                postalCode: address.postal_code || '',
                address_type: address.address_type || 'billing',
              }))
                : [{
                  addressLine1: '',
                  addressLine2: '',
                  addressLine3: '',
                  state: '',
                  city: '',
                  postalCode: '',
                  address_type: 'billing' as const,
                }],
              shippingAddressEnabled: patientData?.addresses?.some((a: any) => a.address_type === 'shipping') || false,
              shippingSameAsBilling: !patientData?.addresses?.some((a: any) => a.address_type === 'shipping'),
              weight: patientData.weight?.toString() || '',
              weight_recorded_date: patientData.weight_recorded_at
                ? format(new Date(patientData.weight_recorded_at), 'MM/dd/yyyy')
                : undefined,
              height: (() => {
                const h = parseFloat(patientData.height?.toString() || '');
                return isNaN(h) || h === 0 ? undefined : h;
              })(),
              blood_pressure_systolic: patientData.blood_pressure_systolic ?? '',
              blood_pressure_diastolic: patientData.blood_pressure_diastolic ?? '',
              blood_pressure_recorded_at: patientData.blood_pressure_recorded_at || '',
              medication_allergies: patientData.medication_allergies || [],
              no_known_allergies:
                !patientData.medication_allergies || patientData.medication_allergies.length === 0,
              medical_history: patientData.medical_history || '',
              preferred_contact_methods: patientData.preferred_contact_methods || {
                email: false,
                sms: false,
                phone: false,
              },
              emergency_contact_phone: patientData.emergency_contact_phone || '',
              insurance_carrier: patientData.insurance_carrier || '',
              insurance_member_id: patientData.insurance_member_id || '',
              insurance_group_id: patientData.insurance_group_id || '',
              animal_owner_name: patientData.animal_owner_name || '',
            });
          } else {
            toast({
              title: 'Error',
              description: 'Failed to fetch patient details. Please try again.',
              variant: 'destructive',
            });
          }
        } catch (error) {
          console.error('Error fetching patient details:', error);
          toast({
            title: 'Error',
            description: 'Failed to fetch patient details. Please try again.',
            variant: 'destructive',
          });
        } finally {
          setIsFetchingPatient(false);
        }
      }
    };

    fetchPatientDetails();
  }, [open, patient?.id]);

  // Reset collapsible state when archetype changes or drawer opens (medication history always open by default)
  useEffect(() => {
    setIsClinicalOpen(false);
    setIsMedicalOpen(true);
  }, [isGuided, open]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className='flex w-full !max-w-[640px] flex-col gap-0 rounded-l-2xl p-0 [&>button]:hidden'
        onInteractOutside={e => {
          // Prevent closing when clicking on Google Places autocomplete dropdown
          const target = e.target as HTMLElement;
          if (target?.closest('.pac-container')) {
            e.preventDefault();
          }
        }}
        onPointerDownOutside={e => {
          // Prevent closing when clicking on Google Places autocomplete dropdown
          const target = e.target as HTMLElement;
          if (target?.closest('.pac-container')) {
            e.preventDefault();
          }
        }}
      >
        {/* Header */}
        <div className='flex items-center justify-between border-b border-slate-200 px-6 py-4'>
          <SheetTitle className='font-semibold text-slate-900' style={{ fontSize: '1.05rem' }}>
            {patient ? 'Edit Patient' : 'Add Patient'}
          </SheetTitle>
          <SheetClose asChild>
            <button className='flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 transition-colors hover:bg-slate-50'>
              <X className='h-5 w-5 text-slate-600' />
            </button>
          </SheetClose>
        </div>

        {/* Content */}
        <div className='flex-1 overflow-y-auto px-6 py-6'>
          {isFetchingPatient ? (
            <div className='flex h-full items-center justify-center'>
              <div className='flex flex-col items-center space-y-4'>
                <div className='h-8 w-8 animate-spin rounded-full border-4 border-brand-teal border-t-transparent' />
                <p className='text-sm text-slate-500'>Loading patient details...</p>
              </div>
            </div>
          ) : (
            <Form {...form}>
              <form
                id='patient-form'
                onSubmit={form.handleSubmit(handleSubmit, handleValidationError)}
                className='space-y-8'
              >
                <BasicPatientInfoSection form={form} isGuided={isGuided} />

                <ContactInformationSection
                  form={form}
                  fields={fields}
                  append={append}
                  remove={remove}
                  createContact={createContact}
                  isGuided={isGuided}
                />

                <AddressSection
                  form={form}
                  addressFields={addressFields}
                  states={states}
                  handleAddressChange={handleAddressChange}
                  appendAddress={appendAddress}
                  removeAddress={removeAddress}
                />

                <InsuranceSection form={form} />

                <ClinicalInformationSection
                  form={form}
                  notesRows={notesRows}
                  setNotesRows={setNotesRows}
                  isGuided={isGuided}
                  isOpen={isClinicalOpen}
                  onOpenChange={setIsClinicalOpen}
                />

                <MedicalHistorySection
                  form={form}
                  isGuided={isGuided}
                  isOpen={isMedicalOpen}
                  onOpenChange={setIsMedicalOpen}
                />

                {(!isGuided || patient) && (
                  <AssignmentSection
                    form={form}
                    practitioners={practitioners}
                    assignedPractitioner={isGuided && practitioners?.length > 0 ? form.getValues('practitioner_id') : assignedPractitioner}
                    isGuided={isGuided}
                  />
                )}
              </form>
            </Form>
          )}
        </div>

        {/* Footer */}
        <div className='flex items-center justify-between border-t border-slate-200 px-6 py-4'>
          <div className='flex items-center gap-2'>
            <Button
              variant='ghost'
              type='button'
              className='font-medium text-slate-700 hover:bg-transparent hover:text-slate-900'
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              variant='outline'
              type='button'
              className='font-medium text-slate-700'
              onClick={handleResetForm}
            >
              Reset
            </Button>
          </div>
          <Button
            type='submit'
            form='patient-form'
            className='h-11 rounded-lg bg-brand-teal px-8 hover:bg-brand-teal/90'
            disabled={isLoading || isFetchingPatient}
          >
            {isLoading ? (
              <div className='flex items-center space-x-2'>
                <div className='h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent' />
                <span>{patient ? 'Updating...' : 'Saving...'}</span>
              </div>
            ) : (
              <span>{patient ? 'Update Patient' : 'Add Patient'}</span>
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default PatientFormDrawer;
